"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Clock, FileCheck, Layers, LifeBuoy, Repeat, ShieldCheck, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const lifecycleServices = [{
  id: "preventive",
  title: "Preventive Maintenance & Calibration",
  icon: Wrench,
  summary: "Scheduled on-site servicing, optical alignment, and traceable calibration to keep instruments operating within strict specifications.",
  features: ["OEM-certified genuine parts replacement", "NIST and ISO/IEC 17025 traceable calibration standards", "Detailed health check diagnostics and wear-level reports", "Pre-scheduled visits to prevent production halts"],
  metric: "99.4%",
  metricLabel: "Uptime guarantee for contracted systems"
}, {
  id: "requalification",
  title: "Periodic Re-qualification (OQ / PQ)",
  icon: ShieldCheck,
  summary: "Comprehensive re-validation workflows following software upgrades, relocations, or annual compliance review cycles.",
  features: ["Pre-approved qualification test protocols", "Full audit trail verification and data integrity checks", "Fast-turnaround execution by GMP-trained engineers", "Audit-ready documentation binder delivered post-service"],
  metric: "",
  metricLabel: "Audit-ready documentation binder"
}, {
  id: "software",
  title: "Software, Firmware & Data Integrity",
  icon: FileCheck,
  summary: "Proactive management of analytical software versions, OS patches, and Data integrity compliance controls.",
  features: ["Controlled change management protocols", "Secure backup and restore verification routines", "Continuous user access and privilege tier review", "Validated software patching and driver updates"],
  metric: "21 CFR",
  metricLabel: "Part 11 compliant lifecycle architecture"
}, {
  id: "sla",
  title: "Priority SLA Technical Support",
  icon: LifeBuoy,
  summary: "Direct telephone and remote technical diagnostics from our dedicated Dublin and UK application engineering centers.",
  features: ["Guaranteed priority on-site response options (24/48 hr)", "Secure remote telemetry and diagnostic assistance", "Direct escalation to factory analytical specialists", "Comprehensive annual service performance reviews"],
  metric: "",
  metricLabel: "Remote technical response available"
}];
const lifecycleSteps = [{
  step: "01",
  title: "Routine Baseline",
  desc: "Post-IQ/OQ/PQ initial parameter benchmarking and establish operational logs."
}, {
  step: "02",
  title: "Proactive Servicing",
  desc: "Scheduled preventive maintenance cycles to identify consumable wear early."
}, {
  step: "03",
  title: "Periodic Recalibration",
  desc: "Standardized re-qualification to maintain GLP/GMP audit compliance status."
}, {
  step: "04",
  title: "End-of-Life Planning",
  desc: "Structured trade-in paths, migration assistance, and tech refresh evaluations."
}];
export default function Lifecycle() {
  const [activeTab, setActiveTab] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const selectedService = lifecycleServices[activeTab] || lifecycleServices[0];
  const ActiveIcon = selectedService.icon;
  return <section id="lifecyclemanagement" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container space-y-16">
        {/* Header Block */}
        <div className="max-w-3xl space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Post-Qualification Support
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Complete lifecycle management for regulated laboratories
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Analytical reliability extends far beyond initial system handover. Our lifecycle agreements protect data integrity, minimize downtime, and ensure uninterrupted compliance across pharmaceutical, chemical, and biomanufacturing sites.
          </p>
        </div>

        {/* Interactive Services Tab Interface */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Navigation Column */}
          <div className="lg:col-span-5 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2">
              Support Modules
            </p>
            <div className="space-y-2">
              {lifecycleServices.map((service, index) => {
              const IconComponent = service.icon;
              const isSelected = activeTab === index;
              return <div key={service.id} data-index={index}>
                    <Button variant={isSelected ? "default" : "outline"} size="lg" onClick={() => setActiveTab(index)} className={`w-full justify-start text-left h-auto py-4 px-4 rounded-xl transition-[color,background-color,border-color,box-shadow] ${isSelected ? "bg-primary text-primary-foreground shadow-md" : "bg-card text-foreground hover:bg-muted/60 border-border"}`}>
                      <div className="flex items-start gap-3 w-full">
                        <div className={`p-2 rounded-lg shrink-0 mt-0.5 ${isSelected ? "bg-primary-foreground/15 text-primary-foreground" : "bg-muted text-foreground"}`}>
                          <IconComponent className="size-5" />
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <div className="font-semibold text-sm sm:text-base leading-snug truncate">
                            {service.title}
                          </div>
                          <div className={`text-xs mt-1 line-clamp-1 ${isSelected ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                            {service.summary}
                          </div>
                        </div>
                        <ArrowRight className={`size-4 shrink-0 transition-transform mt-1 ${isSelected ? "translate-x-0.5" : "opacity-40"}`} />
                      </div>
                    </Button>
                  </div>;
            })}
            </div>

            {/* Quick Contact Prompt Card */}
            <Card className="bg-secondary/50 border-border/80 mt-6 shadow-xs">
              <CardContent className="p-5 flex flex-col gap-3">
                <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                  <Activity className="size-4 text-primary" />
                  Custom Service Level Agreements
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Tailor preventive schedules and response windows to match your facility shifts and audit calendars.
                </p>
                <Button variant="outline" size="sm" asChild className="w-full bg-background font-medium mt-1">
                  <Link to="/ContactPage">
                    Inquire About Service Contracts
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Active Service Detail Column */}
          <div className="lg:col-span-7">
            <motion.div key={selectedService.id} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="h-full">
              <Card className="border-border bg-card shadow-sm h-full flex flex-col justify-between overflow-hidden">
                <div>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="inline-flex items-center gap-2 text-primary text-sm font-semibold">
                        <ActiveIcon className="size-5" />
                        <span>Core Service Capability</span>
                      </div>
                      <div className="text-right">
                        {selectedService.metric && <span className="text-2xl font-bold font-default text-foreground block">
                            {selectedService.metric}
                          </span>}
                        <span className="text-xs text-muted-foreground">
                          {selectedService.metricLabel}
                        </span>
                      </div>
                    </div>
                    <CardTitle className="text-2xl font-default font-bold text-foreground mt-2">
                      {selectedService.title}
                    </CardTitle>
                    <CardDescription className="text-sm sm:text-base text-muted-foreground mt-1">
                      {selectedService.summary}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-6 pt-2">
                    <Separator />
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                        Standard Inclusions & Deliverables
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                        {selectedService.features.map((feature, i) => <div key={i} data-index={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-secondary/40 border border-border/50 text-sm">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span className="text-foreground text-xs sm:text-sm leading-snug">
                              {feature}
                            </span>
                          </div>)}
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-muted/40 border border-border text-xs sm:text-sm text-muted-foreground flex items-center gap-3">
                      <Clock className="size-5 text-primary shrink-0" />
                      <span>
                        All fieldwork is carried out by regional service specialists trained in Ireland and UK laboratory compliance standards.
                      </span>
                    </div>
                  </CardContent>
                </div>

                <div className="p-6 pt-2 bg-card border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                  <span className="text-xs text-muted-foreground text-center sm:text-left">
                    Need support for multi-vendor instruments or older models?
                  </span>
                  <Button variant="default" size="sm" asChild className="w-full sm:w-auto shrink-0 shadow-xs">
                    <Link to="/ServicesPage">
                      View Full Services Catalog
                    </Link>
                  </Button>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>

        {/* Lifecycle Roadmap Diagram */}
        <div className="space-y-6 pt-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <h3 className="text-xl sm:text-2xl font-default font-bold text-foreground">
                Structured Lifecycle Framework
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                A predictable pathway from initial validation to eventual instrument renewal.
              </p>
            </div>
            <Button variant="outline" size="sm" asChild className="self-start sm:self-auto">
              <Link to="/SolutionsPage">
                Explore Technology Solutions
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {lifecycleSteps.map((stepItem, idx) => <div key={stepItem.step} data-index={idx} className="p-5 rounded-xl border border-border bg-card text-card-foreground flex flex-col justify-between space-y-4 hover:border-primary/40 transition-[border-color,box-shadow] shadow-xs">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold px-2.5 py-1 rounded bg-secondary text-secondary-foreground">
                    {stepItem.step}
                  </span>
                  <Repeat className="size-4 text-muted-foreground/60" />
                </div>
                <div>
                  <h4 className="font-semibold text-base text-foreground mb-1">
                    {stepItem.title}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {stepItem.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}